import { EventObject } from "../../events/EventObject.js";
import { generateUniqueId, sanitizeHTML } from "../../utils/utils.js";
import { resolveComplexTemplate } from "../../utils/template/resolveComplexTemplate.js";
import { enhanceHTML } from "../../utils/template/enhanceHTML.js";
import * as templateFunctions from "../popup/templateFunctions/index.js"

export { Layer }

/**
 * Abstract Layer class
 * 
 * @author rhess <robin.hess@awi.de>
 * @memberof vef.map.layer
 */
class Layer extends EventObject {

    /**
     * Set all properties for the layer based on the options object.
     * 
     * @param {object} config 
     * @param {object} cache 
     * @param {string} id 
     */
    constructor(config, cache, id) {
        super({
            "update": [],
            "remove": [],
            "loading": [],
            "load": [],
            "message": [],
            "enable": [],
            "disable": []
        });

        this.uniqueId = id || generateUniqueId();

        // layer metadata containers
        this.config = Object.assign({}, config || {});
        this.cache = Object.assign({}, cache || {});
        this.defaults = {};

        // init default values and define getters and setters
        this.addDefaults_({
            type: "",
            title: "",
            originalTitle: "",
            abstract: "",
            attribution: "",
            comment: "",
            citation: "",
            legendUrl: "",
            dateTimeStart: "",
            dateTimeEnd: "",
            opacity: 1,
            availableCrs: [],
            keywords: [],
            dimensions: {},
            attributeFields: {},
            metadataTemplate: null,
            icon: "fas fa-file",
            timeColumn: null,
            timeResolution: null,
            catalogId: null,
            // state
            expanded: false,
            active: false,
            filterDisabled: false,
            // settings
            styleSettings: false,
            opacitySettings: false,
            colorScaleSettings: false
        });

        this.setAll_("type", "layer");

        // apply the catalogId to the config, if it is only defined in the cache
        if (!("catalogId" in this.config) && ("catalogId" in this.cache)) {
            this.config.catalogId = this.cache.catalogId;
        }

        // internal properties
        this.messages_ = [];
        this.popupEvents_ = [];
        this.mapLayers_ = {};
        this.activeMap_ = null;
        this.crs_ = (this.availableCrs.length > 0) ? this.availableCrs[0] : "";
    }

    setAll_(property, type) {
        this.config[property] = type;
        this.cache[property] = type;
        this.defaults[property] = type;
    }

    /**
     * Helper method to get a property from the config, cache or default container
     * 
     * @param {string} name 
     * @private
     * @returns 
     */
    getProperty_(name) {
        if (name in this.config) return this.config[name];
        if (name in this.cache) return this.cache[name];
        if (name in this.defaults) return this.defaults[name];
        return undefined;
    }

    /**
     * Register the getter and setter for a property and add default values for properties
     * 
     * @private
     * @param {object} defaults 
     */
    addDefaults_(defaults) {
        for (let name in defaults) {
            if (name in this) delete this[name];
            this.defaults[name] = defaults[name];
            Object.defineProperty(this, name, {
                get() { return this.getProperty_(name); },
                set(val) { this.config[name] = val; },
                configurable: true
            });
        }
    }

    /**
     * Register the getter and setter for a property and add default values for properties
     * in the exported config object. Existing values are not overwritten
     * 
     * @private
     * @param {object} config 
     */
    addConfig_(config) {
        for (let name in config) {
            if (name in this) delete this[name];
            if (!(name in this.config)) this.config[name] = config[name];
            Object.defineProperty(this, name, {
                get() { return this.config[name]; },
                set(val) { this.config[name] = val; },
                configurable: true
            });
        }
    }

    getConfig_(container) {
        container = JSON.parse(JSON.stringify(container));

        for (let key in container) {
            if (
                (container[key] === null) ||
                (((typeof container[key] == "string") || Array.isArray(container[key])) && (container[key].length == 0)) ||
                ((typeof container[key] == "object") && (Object.keys(container[key]).length == 0))

            ) delete container[key];
        }

        // remove deprecated keys
        if ("settings" in container) delete container.settings;
        if ("filter" in container) delete container.filter;
        // remove default keys
        if (container.active === false) delete container.active;
        if (container.expanded === false) delete container.expanded;

        return container;
    }

    getConfig() {
        const config = this.getConfig_(this.config);
        if (("catalogId" in config) && ("type" in config)) delete config.type;
        return config;
    }

    getCache() {
        return this.getConfig_(this.cache);
    }

    /**
     * Switch the projection for the visualization
     * of the leaflet layer. CRS hast to be included in availableCRS.
     * 
     * @param {string} crs target crs code
     */
    setProjection(crs) {
        this.crs_ = crs;
        // implement actual projection switching in child classes
        if (this.availableCrs.includes(crs) && (crs.replace(":", "") in L.CRS)) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Get Template for printing the layer metadata in a table
     * Property names are mapped for better readability.
     */
    getMetadataTemplate() {
        return `
### Layer Abstract
{parseMarkdown(layer.abstract, "105")}

### Layer Information
| | |
|--|--|
| Title | {layer.title} |
| Original Title | {layer.originalTitle} |
| Name | {layer.name} |
| Type | {layer.type} |
| Attribution | {layer.attribution} |
| Comment | {layer.comment} |
| Citation | {layer.citation} |
| Spatial Reference System | {joinArray(layer.availableCrs)} |
| Keywords | {joinArray(layer.keywords)} |
| Column Name | {joinArray(layer.columnData)} |

### Dataset Description
| | |
|--|--|
| Citation | {datasetDescription.citation} |
| URL | {datasetDescription.url} |
| License | {datasetDescription.license} |
| Events | {joinArray(datasetDescription.events)} |
| Parameters | {joinArray(datasetDescription.parameters)} |
| Projects | {joinArray(datasetDescription.projects)} |
| Further Details | {joinArray(datasetDescription.furtherDetails)} |
| Related To | {joinArray(datasetDescription.relatedTo)} |

### Parameter
| | |
|--|--|
| Name | {sensorMetadata.parameters["0"].name} |
| Type | {sensorMetadata.parameters["0"].code} |
| Unit | {sensorMetadata.parameters["0"].unit} |
| Aggregated By | {layer.aggregate} |
| Limit | {layer.limit} |

### Sensor Metadata
| | |
|--|--|
| Short Name | {sensorMetadata.metadata.shortName} |
| Model | {sensorMetadata.metadata.model} |
| Manufacturer | {sensorMetadata.metadata.manufacturer} |
| Link | {concatIfDefined("https://sensor.awi.de/?id=", this.sensorMetadata.metadata.sensorLinkId)} |
| Code | {sensorMetadata.metadata.code} |
| Type | {sensorMetadata.metadata.type} |
| First Date | {sensorMetadata.metadata.firstDate} |
| Last Date | {sensorMetadata.metadata.lastDate} |
| Last Modified | {sensorMetadata.metadata.lastModified} |

### Service Abstract
{parseMarkdown(layer.serviceAbstract, "105")}

### Service Information
| | |
|--|--|
| Title | {layer.serviceTitle} |
| Name | {layer.serviceName} |
| URL | [{layer.capabilitiesUrl}]({layer.capabilitiesUrl}) |
| URL | [{layer.url}]({layer.url}) |
| Version | {layer.serviceVersion} |
| Software | {layer.serviceSoftware} |
| Filter Type | {layer.filterType} |
| Fees | {layer.serviceFees} |
| Access Constraints | {layer.serviceAccessConstraints} |
| Keywords | {joinArray(layer.serviceKeywords)} |

### Legend Graphic
{displayLayerLegend()}
        `;
    }

    /**
     * Print the Metadata from this layer as html.
     */
    printMetadata(layer) {
        const template = this.getMetadataTemplate();
        const container = resolveComplexTemplate(template, {
            layer: layer || this,
            datasetDescription: this?.dataFrame?.description || {},
            sensorMetadata: this?.sensorMetadata || {},
        }, templateFunctions, {
            markdown: true,
            postProcess: true
        });
        enhanceHTML(container, {});
        return container;
    }

    /**
     * get the events that can be triggered by buttons in a popup
     */
    getPopupEvents() {
        return this.popupEvents_.slice();
    }

    /**
     * add an event for the buttons in the popup
     */
    addPopupEvent(title, callback) {
        if ((typeof title == "string") && (typeof callback == "function")) {
            this.popupEvents_.push({
                title: title,
                callback: callback
            });
        }
    }

    /**
     * returns the map layer for the
     * specific map implementation
     * 
     * @param {string} type 
     * @returns {object} map layer for framework
     */
    getMapLayer(type) {
        return (type in this.mapLayers_)
            ? this.mapLayers_[type]
            : this.createMapLayer_(type);
    }

    /**
     * enables the map layer for the
     * specific map implementation
     * 
     * @param {string} type 
     */
    enable(type) {
        if (type in this.mapLayers_) {
            this.active = true;
            this.updateMapLayer_(type);
            return true;
        }

        return false;
    }

    /**
     * Unset the active state of the layer
     */
    disable() {
        this.active = false;
    }

    /**
     * create a map layer based on the service options
     * 
     * @param {string} type e.g. "leaflet"
     * @private
     */
    createMapLayer_() { return null }

    /**
     * Helper method to update the mapLayer according to
     * the current state. Might be necessary after switching the map type
     * 
     * @private
     */
    updateMapLayer_() { }

    /**
     * Returns the Legend Graphic for this layer.
     * May depend on current color scale.
     * 
     * @returns html <img>-tag (null or undefined if there is no legend graphic)
     */
    getLegendGraphic() { }

    /**
     * Set Filter without applying it
     *  
     * @param {object} config generic filter config 
     */
    setFilter(config) {
        this.filter = config;
    }

    /**
     * Apply the defined filter. Uses generic
     * filter syntax and ttransforms it internally
     */
    applyFilter() { }

    /**
     * Set the style of the layer.
     * 
     * @param {string} styleName style name
     * @returns {boolean} returns true if successful
     */
    setStyle(styleName) { }

    /**
     * Get the active style of the layer.
     */
    getStyle() { }

    /**
     * Get the transparency value
     * 
     * @returns {number} number between 0 and 1
     */
    getOpacity() {
        return this.opacity;
    }

    /**
     * Set the transparency between 0 and 1
     * @param {number} opacity style name
     */
    setOpacity(opacity) { }

    /**
     * Set the colorscale for the layer
     * 
     * @param colorScale 
     */
    setColorScale(colorScale) { }

    /**
     * Get the active colorscale for the layer
     */
    getColorScale() { }

    /**
     * Reload the Layer on the map
     */
    reload() { }

    /**
     * Disose the Layer
     */
    dispose() { }

    /**
     * bring layer to back on the map
     */
    bringToBack() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.bringToBack();
                break;
        }
    }

    /**
     * bring layer to front on the map
     */
    bringToFront() {
        if (!(this.activeMap_ in this.mapLayers_)) return;

        switch (this.activeMap_) {
            case "leaflet":
                this.mapLayers_.leaflet.bringToFront();
                break;
        }
    }

    /**
     * Get the bounding box as an object
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        return {
            min: { y: -90, x: -180 },
            max: { y: 90, x: 180 },
            crs: "CRS:84"
        }
    }

    /**
     * Get all active messages
     * 
     * @returns {object[]} messages
     */
    getMessages() {
        // implementation of layer-specific messages needs to be done in child classes
        return [...this.messages_];
    }
}